/**
 * Notifications on THIS device — the switch in the Integrations modal.
 *
 * Two ways a device gets them, and the page drives both (server/notify.ts):
 *
 * - A browser subscribes to Web Push through the service worker (public/sw.js).
 *   The subscription is the switch: while the browser holds one, it is on.
 * - The Android app's WebView has no Push API. The page registers the phone
 *   with the server and hands the id to the app (`window.todosAndroid`), whose
 *   background job collects what has queued for it.
 */
import { api } from "./api.js";

/** What the Android app puts on the page (NotifyBridge in android/…/MainActivity.kt). */
interface AndroidBridge {
  /** The id this phone polls with, or "" when notifications are off. */
  notifyDevice(): string;
  /** Start polling as `deviceId`, asking Android's permission first. Answers with a `todos-notify` event. */
  enableNotifications(deviceId: string): void;
  disableNotifications(): void;
  /** The launcher icon's repo shortcuts, as JSON `[{team, repo}]` (lib/appShortcuts.ts). Absent on an app from before them. */
  setRepoShortcuts?(json: string): void;
  /** Look for a newer build and say what was found (Updater.kt). Absent in apps built before it. */
  checkForUpdate?(): void;
}

declare global {
  interface Window {
    todosAndroid?: AndroidBridge;
  }
}

/** Fired by the app once it knows whether Android let it notify; `detail` is true when on. */
const ANDROID_RESULT = "todos-notify";

export type NotifySupport = "android" | "webpush" | "none";

export function notifySupport(): NotifySupport {
  if (window.todosAndroid) return "android";
  const push = isSecureContext && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
  return push ? "webpush" : "none";
}

async function currentSubscription(): Promise<PushSubscription | null> {
  const reg = await navigator.serviceWorker.getRegistration("/");
  return (await reg?.pushManager.getSubscription()) ?? null;
}

/** Tell the server about this browser. Idempotent, and moves it to whoever is signed in. */
async function register(sub: PushSubscription): Promise<string> {
  const { endpoint, keys } = sub.toJSON();
  const { id } = await api.post<{ id: string }>("/api/notify/webpush", { endpoint, keys });
  return id;
}

/** The key the browser subscribes with arrives base64url; subscribe() takes bytes. */
function bytes(b64url: string): Uint8Array<ArrayBuffer> {
  const bin = atob(b64url.replace(/-/g, "+").replace(/_/g, "/"));
  const out = new Uint8Array(new ArrayBuffer(bin.length));
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/**
 * Whether notifications are on here. Checked against the server too, so a
 * device it has forgotten (the phone went a month without polling) reads as
 * off rather than as a switch that does nothing.
 */
export async function notificationsOn(): Promise<boolean> {
  const android = window.todosAndroid;
  if (android) {
    const id = android.notifyDevice();
    if (!id) return false;
    const { registered } = await api.get<{ registered: boolean }>(`/api/notify/devices/${encodeURIComponent(id)}`);
    if (!registered) android.disableNotifications();
    return registered;
  }
  if (notifySupport() !== "webpush") return false;
  const sub = await currentSubscription();
  if (!sub) return false;
  if (Notification.permission !== "granted") {
    // Permission taken back in the browser's settings: the subscription is dead.
    await sub.unsubscribe();
    return false;
  }
  // Re-sent every time, so the server's copy follows whoever is signed in now.
  await register(sub);
  return true;
}

/** Switch notifications on here. Throws with something to show when it cannot. */
export async function enableNotifications(): Promise<void> {
  const android = window.todosAndroid;
  if (android) {
    const { id } = await api.post<{ id: string }>("/api/notify/poll");
    const allowed = new Promise<boolean>((resolve) =>
      window.addEventListener(ANDROID_RESULT, (ev) => resolve((ev as CustomEvent<boolean>).detail === true), { once: true }),
    );
    android.enableNotifications(id);
    if (!(await allowed)) {
      await api.del(`/api/notify/devices/${encodeURIComponent(id)}`).catch(() => undefined);
      throw new Error("Android is not letting todos notify — allow it in the app's settings");
    }
    return;
  }
  const permission = await Notification.requestPermission();
  if (permission === "denied") throw new Error("Notifications are blocked for this site in the browser's settings");
  if (permission !== "granted") throw new Error("Notifications were not allowed");
  await navigator.serviceWorker.register("/sw.js");
  const reg = await navigator.serviceWorker.ready;
  const { publicKey } = await api.get<{ publicKey: string }>("/api/notify/vapid");
  const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: bytes(publicKey) });
  await register(sub);
}

/** Switch them off here. Best effort on the server's side: the device goes quiet either way. */
export async function disableNotifications(): Promise<void> {
  const android = window.todosAndroid;
  if (android) {
    const id = android.notifyDevice();
    android.disableNotifications();
    if (id) await api.del(`/api/notify/devices/${encodeURIComponent(id)}`).catch(() => undefined);
    return;
  }
  if (notifySupport() !== "webpush") return;
  const sub = await currentSubscription();
  if (!sub) return;
  // The server knows the browser by id; registering answers with it.
  const id = await register(sub).catch(() => null);
  if (id) await api.del(`/api/notify/devices/${encodeURIComponent(id)}`).catch(() => undefined);
  await sub.unsubscribe();
}
